import { createApi } from '@reduxjs/toolkit/query/react';

import { authQuery } from './baseQuery';
import type {
  ICustomerPause,
  CustomerPauseStatus,
  ICustomerPauseEvent,
  ICancelCustomerPause,
  ICreateCustomerPause,
  IUpdateCustomerPause,
  ICustomerPauseStatusChange,
} from 'src/types/customerPauses';

export const customerPausesAPISlice = createApi({
  reducerPath: 'presentation-service-customer-pauses-api',
  tagTypes: ['CustomerPauses'],
  baseQuery: (args: any, api: any, extraOptions: any) =>
    authQuery(
      args,
      api,
      (extraOptions = { ...extraOptions, baseUrl: window.RUNTIME_CONFIG.PRESENTATION_SERVICE })
    ),
  endpoints: (builder) => ({
    getCustomerPauses: builder.query<
      ICustomerPause[],
      { customerId: string; status?: CustomerPauseStatus }
    >({
      query: ({ customerId, status }) =>
        `/customers/${customerId}/pauses${status ? `?status=${status}` : ''}`,
      providesTags: ['CustomerPauses'],
    }),

    getCustomerPauseById: builder.query<ICustomerPause, { customerId: string; pauseId: string }>({
      query: ({ customerId, pauseId }) => `/customers/${customerId}/pauses/${pauseId}`,
      providesTags: ['CustomerPauses'],
    }),

    getCustomerPauseEvents: builder.query<
      ICustomerPauseEvent[],
      { customerId: string; pauseId?: string }
    >({
      query: ({ customerId, pauseId }) =>
        `/customers/${customerId}/pauses/events${pauseId ? `?pauseId=${pauseId}` : ''}`,
      providesTags: ['CustomerPauses'],
    }),

    getCustomerPauseStatusChanges: builder.query<
      ICustomerPauseStatusChange[],
      { customerId: string }
    >({
      query: ({ customerId }) => `/customers/${customerId}/pauses/status-changes`,
      providesTags: ['CustomerPauses'],
    }),

    createCustomerPause: builder.mutation<
      ICustomerPause,
      { customerId: string; requestData: ICreateCustomerPause }
    >({
      query: ({ customerId, requestData }) => ({
        url: `/customers/${customerId}/pauses`,
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['CustomerPauses'],
    }),

    updateCustomerPause: builder.mutation<
      ICustomerPause,
      { customerId: string; pauseId: string; requestData: IUpdateCustomerPause }
    >({
      query: ({ customerId, pauseId, requestData }) => ({
        url: `/customers/${customerId}/pauses/${pauseId}`,
        method: 'PATCH',
        body: requestData,
      }),
      invalidatesTags: ['CustomerPauses'],
    }),

    cancelCustomerPause: builder.mutation<
      ICustomerPause,
      { customerId: string; pauseId: string; requestData: ICancelCustomerPause }
    >({
      query: ({ customerId, pauseId, requestData }) => ({
        url: `/customers/${customerId}/pauses/${pauseId}/cancel`,
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['CustomerPauses'],
    }),
  }),
});

export const {
  useGetCustomerPausesQuery,
  useGetCustomerPauseByIdQuery,
  useGetCustomerPauseEventsQuery,
  useGetCustomerPauseStatusChangesQuery,
  useCreateCustomerPauseMutation,
  useUpdateCustomerPauseMutation,
  useCancelCustomerPauseMutation,
} = customerPausesAPISlice;
